import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import { Task } from "@saleor/containers/BackgroundTasks/types";
import { UpdateVoucherGroupStatus } from "@saleor/discounts/types/UpdateVoucherGroupStatus";
import useBackgroundTask from "@saleor/hooks/useBackgroundTask";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import { DiscountStatusEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import { getArrayQueryParam } from "@saleor/utils/urls";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import VoucherListPage from "../../components/VoucherListPage";
import {
  TypedUpdateVoucherGroupStatus,
  TypedVoucherBulkDeactivate
} from "../../mutations";
import { useBulkVoucherTabListQuery, useVoucherListQuery } from "../../queries";
import { VoucherBulkDeactivate } from "../../types/VoucherBulkDeactivate";
import {
  bulkVoucherListUrl,
  voucherAddUrl,
  voucherListUrl,
  VoucherListUrlDialog,
  VoucherListUrlQueryParams,
  voucherUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getBulkFilterVariables,
  getFilterOpts,
  getFilterQueryParam,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";
interface VoucherListProps {
  params: VoucherListUrlQueryParams;
}

export const VoucherList: React.FC<VoucherListProps> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const shop = useShop();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    getArrayQueryParam(params.ids)
  );
  const { updateListSettings, settings } = useListSettings(
    ListViews.VOUCHER_LIST
  );
  const intl = useIntl();
  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params)
    }),
    [params]
  );

  const bulkQueryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getBulkFilterVariables(params),
      sort: getSortQueryVariables(params)
    }),
    [params]
  );

  const { data, loading, refetch: refetchVoucherList } = useVoucherListQuery({
    displayLoader: true,
    skip: Boolean(params.activeTab && params.activeTab !== "0"),
    variables: {
      ...queryVariables,
      filter: {
        ...queryVariables.filter,
        hasGroup: false
      }
    }
  });

  const {
    data: voucherGroup,
    loading: voucherGroupLoading,
    refetch: refetchBulkVoucherTabList
  } = useBulkVoucherTabListQuery({
    displayLoader: true,
    skip: params.activeTab !== "1",
    variables: bulkQueryVariables
  });

  const voucherTab = [
    ...[
      {
        name: "Single Voucher"
      },
      {
        name: "Bulk Voucher"
      }
    ]
  ];

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? voucherTab.length
        : 0
      : parseInt(params.activeTab, 0);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: voucherListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const handleSearchChange = (text: string) => {
    navigate(voucherListUrl({ ...params, query: text }));
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    VoucherListUrlDialog,
    VoucherListUrlQueryParams
  >(navigate, voucherListUrl, params);

  const handleTabChange = (tab: number | string) => {
    reset();
    navigate(
      voucherListUrl({
        ...params,
        activeTab: tab.toString(),
        after: null,
        before: null,
        ...voucherTab[tab].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(voucherListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(voucherTab.length + 1);
  };

  const canOpenBulkActionDialog = maybe(() => params.ids.length > 0);

  const pageInfoValue =
    params.activeTab !== "1"
      ? maybe(() => data.vouchers.pageInfo)
      : maybe(() => voucherGroup.voucherGroups.pageInfo);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    pageInfoValue,
    paginationState,
    params
  );

  const handleVoucherBulkDeactivate = async (data: VoucherBulkDeactivate) => {
    if (data.voucherBulkDeactivate.errors.length === 0) {
      reset();
      closeModal();
      refetchVoucherList();
    } else if (data.voucherBulkDeactivate.errors.length !== 0) {
      closeModal();
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Unable to deactivate. Voucher promo is ongoing."
        })
      });
      reset();
      refetchVoucherList();
    }
  };

  const [bulkLoading, setBulkLoading] = useState(false);
  const { queue } = useBackgroundTask();
  const handleVoucherGroupDeactivate = (data: UpdateVoucherGroupStatus) => {
    if (data.updateVoucherGroupStatus.errors.length === 0) {
      queue(Task.BULK_VOUCHER_UPDATE, {
        id: data.updateVoucherGroupStatus.voucherJobBulk.id,
        onResponseData: jobData => {
          if (jobData && jobData.data) {
            const status = jobData.data.voucherJobBulk.status;
            if (status === "PENDING") {
              setBulkLoading(true);
            } else if (status === "SUCCESS") {
              setBulkLoading(false);
              reset();
              closeModal();
              refetchBulkVoucherTabList();
            }
          }
        }
      });
    } else if (data.updateVoucherGroupStatus.errors.length !== 0) {
      closeModal();
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Unable to deactivate. Voucher promo is ongoing."
        })
      });
      reset();
      refetchBulkVoucherTabList();
    }
  };

  const handleSort = createSortHandler(navigate, voucherListUrl, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "PHP");

  React.useEffect(
    () =>
      navigate(
        voucherListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  return (
    <TypedVoucherBulkDeactivate onCompleted={handleVoucherBulkDeactivate}>
      {(voucherBulkDeactivate, voucherBulkDeactivateOpts) => {
        const onVoucherBulkDeactivate = () =>
          voucherBulkDeactivate({
            variables: {
              ids: params.ids
            }
          });
        return (
          <TypedUpdateVoucherGroupStatus
            onCompleted={handleVoucherGroupDeactivate}
          >
            {(
              voucherGroupStatusDeactivate,
              voucherGroupStatusDeactivateOpts
            ) => {
              const onVoucherGroupDeactivate = () =>
                voucherGroupStatusDeactivate({
                  variables: {
                    ids: Object.values(params.ids),
                    status: DiscountStatusEnum.INACTIVE
                  }
                });
              return (
                <>
                  <WindowTitle
                    title={intl.formatMessage(sectionNames.vouchers)}
                  />
                  <VoucherListPage
                    brands={null}
                    currencySymbol={currencySymbol}
                    activeTab={params.activeTab}
                    currentTab={currentTab}
                    filterOpts={getFilterOpts(params)}
                    initialSearch={params.query || ""}
                    onSearchChange={handleSearchChange}
                    onFilterChange={filter => changeFilters(filter, true)}
                    onAll={resetFilters}
                    onTabChange={handleTabChange}
                    onTabDelete={() => openModal("delete-search")}
                    onTabSave={() => openModal("save-search")}
                    tabs={voucherTab.map(tab => tab.name)}
                    defaultCurrency={maybe(() => shop.defaultCurrency)}
                    settings={settings}
                    vouchers={maybe(() =>
                      data.vouchers.edges.map(edge => edge.node)
                    )}
                    voucherGroups={maybe(() =>
                      voucherGroup.voucherGroups.edges.map(edge => edge.node)
                    )}
                    disabled={
                      params.activeTab === "0" ? loading : voucherGroupLoading
                    }
                    onArrowClick={id => () => navigate(bulkVoucherListUrl(id))}
                    pageInfo={pageInfo}
                    onAdd={() => navigate(voucherAddUrl)}
                    onNextPage={loadNextPage}
                    onPreviousPage={loadPreviousPage}
                    onUpdateListSettings={updateListSettings}
                    onRowClick={id => () => navigate(voucherUrl(id))}
                    onSort={handleSort}
                    isChecked={isSelected}
                    selected={listElements.length}
                    sort={getSortParams(params)}
                    toggle={toggle}
                    toggleAll={toggleAll}
                    customAction={undefined}
                    params={params}
                    paginationState={paginationState}
                    toolbar={
                      params.activeTab === "0" && listElements.length > 0 ? (
                        <Button
                          color="primary"
                          onClick={() =>
                            openModal("deactivate", {
                              ids: listElements
                            })
                          }
                        >
                          <FormattedMessage
                            defaultMessage="Deactivate"
                            description="deactivate button"
                          />
                        </Button>
                      ) : params.activeTab === "1" &&
                        listElements.length > 0 ? (
                        <Button
                          color="primary"
                          onClick={() =>
                            openModal("deactivate", {
                              ids: listElements
                            })
                          }
                        >
                          <FormattedMessage
                            defaultMessage="Deactivate"
                            description="deactivate button"
                          />
                        </Button>
                      ) : (
                        undefined
                      )
                    }
                  />
                  <ActionDialog
                    confirmButtonState={
                      params.activeTab === "0"
                        ? voucherBulkDeactivateOpts.status
                        : voucherGroupStatusDeactivateOpts.status
                    }
                    onClose={closeModal}
                    onConfirm={() => {
                      if (params.activeTab === "0") {
                        onVoucherBulkDeactivate();
                      } else {
                        onVoucherGroupDeactivate();
                      }
                    }}
                    open={
                      params.action === "deactivate" && canOpenBulkActionDialog
                    }
                    title={intl.formatMessage({
                      defaultMessage: "Deactivate Vouchers",
                      description: "dialog header"
                    })}
                    variant="delete"
                    confirmButtonLabel="Deactivate"
                  >
                    {bulkLoading}
                    {maybe(() => canOpenBulkActionDialog, false) && (
                      <DialogContentText>
                        <FormattedMessage
                          defaultMessage="{counter,plural,one{Are you sure you want to deactivate this voucher?} other{Are you sure you want to deactivate {displayQuantity} vouchers?}}"
                          description="dialog content"
                          values={{
                            counter: params.ids.length,
                            displayQuantity: (
                              <strong>{params.ids.length}</strong>
                            )
                          }}
                        />
                      </DialogContentText>
                    )}
                  </ActionDialog>
                  <SaveFilterTabDialog
                    open={params.action === "save-search"}
                    confirmButtonState="default"
                    onClose={closeModal}
                    onSubmit={handleTabSave}
                  />
                  <DeleteFilterTabDialog
                    open={params.action === "delete-search"}
                    confirmButtonState="default"
                    onClose={closeModal}
                    onSubmit={handleTabDelete}
                    tabName={maybe(
                      () => voucherTab[currentTab - 1].name,
                      "..."
                    )}
                  />
                </>
              );
            }}
          </TypedUpdateVoucherGroupStatus>
        );
      }}
    </TypedVoucherBulkDeactivate>
  );
};
export default VoucherList;
